import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { flag } from '../utils/countryFlags';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Slider } from '../components/ui/slider';
import { 
  Search, Plane, CalendarIcon, Star, BadgeCheck, X, Luggage, MapPin, ArrowLeft,
  Shield, Headphones, TrendingUp, Clock, ChevronDown, SlidersHorizontal, Package, PlusCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERY_DELAY_LABELS = {
  '1_day': '24h',
  '2_3_days': '2-3 jours',
  '1_week': '1 semaine',
  '2_weeks': '2 semaines',
};

const BAGGAGE_TYPE_SHORT = {
  kilos: 'kg',
  sacs: 'sac',
  enveloppes: 'env.',
  sac_23kg: 'sac 23kg',
  telephones: 'tel.',
  ordinateurs: 'ordi.',
};

function getDaysUntil(dateStr) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T12:00:00');
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

function getAvailableKg(listing) {
  if (listing.baggage_items?.length) {
    const kiloItem = listing.baggage_items.find(i => i.type === 'kilos');
    if (kiloItem) return kiloItem.available_quantity ?? kiloItem.quantity;
  }
  return listing.available_quantity ?? 0;
}

function getMinPrice(listing) {
  if (listing.baggage_items?.length) {
    const prices = listing.baggage_items
      .filter(i => i.price_per_unit > 0)
      .map(i => i.price_per_unit);
    return prices.length ? Math.min(...prices) : 0;
  }
  return listing.price_per_unit ?? 0;
}

// ─── HERO SECTION ────────────────────────────────────────────────────
const HeroSection = ({ filters, setFilters, onSearch, navigate, user }) => (
  <section className="relative overflow-hidden" data-testid="hero-section">
    {/* Background */}
    <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
    <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/5 blur-[120px]" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-6 sm:pb-10">
      {/* Top row: Back + Create */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors group"
          data-testid="listings-back-btn"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm tracking-wide uppercase">Retour</span>
        </button>
        <Button
          size="sm"
          className="h-9 px-4 bg-muted/50 hover:bg-muted border border-border hover:border-[#D4AF37]/30 text-foreground font-medium rounded-xl transition-all text-xs sm:text-sm"
          onClick={() => user ? navigate('/create-listing') : navigate('/login')}
          data-testid="create-listing-btn"
        >
          <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
          Créer une annonce
        </Button>
      </div>

      {/* Title — compact on mobile */}
      <div className="max-w-3xl mb-5 sm:mb-8">
        <h1
          className="font-heading text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15] mb-2"
          data-testid="hero-title"
        >
          Envoyez un colis entre le{' '}
          <span className="text-[#D4AF37]">Cameroun</span> et le{' '}
          <span className="text-[#D4AF37]">Canada</span>
        </h1>
        <p className="text-sm text-muted-foreground hidden sm:block">
          Réservez en quelques clics avec des voyageurs vérifiés
        </p>
      </div>

      {/* Search Bar — horizontal on mobile */}
      <div
        className="bg-muted/40 border border-border rounded-2xl p-3 backdrop-blur-sm"
        data-testid="hero-search-bar"
      >
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            <div className="relative">
              <Plane className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#D4AF37]" />
              <Input
                placeholder="Départ"
                value={filters.departure_city}
                onChange={(e) => setFilters(f => ({ ...f, departure_city: e.target.value }))}
                className="pl-9 h-10 sm:h-12 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20 rounded-xl text-sm"
                data-testid="filter-departure"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#D4AF37]" />
              <Input
                placeholder="Destination"
                value={filters.destination_city}
                onChange={(e) => setFilters(f => ({ ...f, destination_city: e.target.value }))}
                className="pl-9 h-10 sm:h-12 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20 rounded-xl text-sm"
                data-testid="filter-destination"
              />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 sm:h-12 justify-start bg-muted/50 border-border text-foreground hover:bg-muted hover:text-foreground rounded-xl col-span-2 sm:col-span-1 text-sm"
                  data-testid="filter-date"
                >
                  <CalendarIcon className="mr-2 h-3.5 w-3.5 text-[#D4AF37]" />
                  {filters.travel_date ? (
                    format(filters.travel_date, 'dd MMM yyyy', { locale: fr })
                  ) : (
                    <span className="text-muted-foreground">Date de voyage</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.travel_date}
                  onSelect={(date) => setFilters(f => ({ ...f, travel_date: date }))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <Button
            className="h-10 sm:h-12 px-6 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-[#D4AF37]/20"
            onClick={onSearch}
            data-testid="search-btn"
          >
            <Search className="w-4 h-4 mr-2" />
            Rechercher
          </Button>
        </div>
      </div>

      {/* Trust Badges — hidden on mobile to save space */}
      <div className="hidden sm:flex flex-wrap gap-4 sm:gap-6 mt-6" data-testid="trust-badges">
        {[
          { icon: Shield, label: 'Paiement sécurisé' },
          { icon: Package, label: 'KACOS gère la logistique' },
          { icon: BadgeCheck, label: 'Voyageurs vérifiés' },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-2 text-muted-foreground">
            <div className="w-7 h-7 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />
            </div>
            <span className="text-xs sm:text-sm">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── FILTER BAR ──────────────────────────────────────────────────────
const FilterBar = ({ filters, setFilters, onSearch, onClear, count, showFilters, setShowFilters, allListings }) => {
  const countries = useMemo(() => {
    const dep = new Set();
    const dest = new Set();
    allListings.forEach(l => {
      if (l.departure_country) dep.add(l.departure_country);
      if (l.destination_country) dest.add(l.destination_country);
    });
    return { departure: [...dep].sort(), destination: [...dest].sort() };
  }, [allListings]);

  const hasActive = filters.departure_country || filters.destination_country || filters.max_price || filters.verified_only;

  return (
    <div className="bg-background border-b border-border sticky top-16 z-30" data-testid="filter-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-muted-foreground" data-testid="results-count">
            <span className="text-[#D4AF37] font-semibold">{count}</span>{' '}
            offre{count > 1 ? 's' : ''} disponible{count > 1 ? 's' : ''}
          </p>
          <button
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#D4AF37] transition-colors"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filtres avancés
            <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-border" data-testid="advanced-filters">
            <Select
              value={filters.departure_country || 'all'}
              onValueChange={(v) => setFilters(f => ({ ...f, departure_country: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="h-10 bg-muted/40 border-border text-foreground text-sm rounded-lg" data-testid="filter-departure-country">
                <SelectValue placeholder="Pays départ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les pays</SelectItem>
                {countries.departure.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.destination_country || 'all'}
              onValueChange={(v) => setFilters(f => ({ ...f, destination_country: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="h-10 bg-muted/40 border-border text-foreground text-sm rounded-lg" data-testid="filter-destination-country">
                <SelectValue placeholder="Pays destination" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les pays</SelectItem>
                {countries.destination.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Prix max</span>
                <span className="text-xs font-medium text-[#D4AF37]">
                  {filters.max_price ? `${filters.max_price} CAD` : 'Tous'}
                </span>
              </div>
              <Slider
                defaultValue={[0]}
                value={[filters.max_price || 0]}
                max={500}
                step={5}
                onValueChange={([v]) => setFilters(f => ({ ...f, max_price: v || '' }))}
                className="[&_[role=slider]]:bg-[#D4AF37] [&_[role=slider]]:border-[#D4AF37] [&_.bg-primary]:bg-[#D4AF37]"
                data-testid="filter-max-price"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                  filters.verified_only
                    ? 'border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'border-border bg-muted/40 text-muted-foreground hover:border-white/20'
                }`}
                onClick={() => setFilters(f => ({ ...f, verified_only: !f.verified_only }))}
                data-testid="filter-verified"
              >
                <BadgeCheck className="w-4 h-4" />
                Vérifié
              </button>
              {hasActive && (
                <button
                  className="text-xs text-muted-foreground hover:text-foreground/70 transition-colors flex items-center gap-1"
                  onClick={onClear}
                  data-testid="clear-filters-btn"
                >
                  <X className="w-3 h-3" />
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── LISTING CARD ────────────────────────────────────────────────────
const ListingCard = ({ listing, onClick }) => {
  const daysUntil = getDaysUntil(listing.travel_date);
  const availKg = getAvailableKg(listing);
  const minPrice = getMinPrice(listing);
  const isUrgent = daysUntil <= 3 && daysUntil >= 0;
  const isLowStock = availKg > 0 && availKg <= 5;
  const delayLabel = DELIVERY_DELAY_LABELS[listing.delivery_delay] || listing.delivery_delay;

  return (
    <div
      className="group relative bg-muted/30 hover:bg-muted/50 border border-border hover:border-[#D4AF37]/30 rounded-2xl p-5 sm:p-6 transition-all duration-300 cursor-pointer"
      onClick={onClick}
      data-testid={`listing-${listing.id}`}
    >
      {/* Urgency/Low stock strip */}
      {(isUrgent || isLowStock) && (
        <div className="absolute top-0 left-6 right-6 h-[2px] rounded-b-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-80" />
      )}

      <div className="flex flex-col lg:flex-row lg:items-start gap-5">
        {/* Left: Route + Details */}
        <div className="flex-1 min-w-0">
          {/* Route header */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center gap-1.5" data-testid={`listing-route-${listing.id}`}>
              <span className="inline-flex items-center">
                {flag(listing.departure_country)}
              </span>
              <span className="font-heading text-lg font-semibold text-foreground">
                {listing.departure_city}
              </span>
            </div>
            <span className="text-[#D4AF37] text-lg mx-1">&rarr;</span>
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center">
                {flag(listing.destination_country)}
              </span>
              <span className="font-heading text-lg font-semibold text-foreground">
                {listing.destination_city}
              </span>
            </div>

            {listing.seller_verified && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium" data-testid={`listing-verified-${listing.id}`}>
                <BadgeCheck className="w-3 h-3" />
                Vérifié
              </span>
            )}
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-1.5">
              <CalendarIcon className="w-3.5 h-3.5 text-[#D4AF37]/70" />
              {new Date(listing.travel_date + 'T12:00:00').toLocaleDateString('fr-FR', {
                weekday: 'short',
                day: 'numeric',
                month: 'long',
              })}
            </span>
            <span className="flex items-center gap-1.5">
              <Plane className="w-3.5 h-3.5 text-[#D4AF37]/70" />
              {listing.airline}
            </span>
            {delayLabel && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#D4AF37]/70" />
                Récupération : {delayLabel}
              </span>
            )}
          </div>

          {/* Baggage items */}
          <div className="flex flex-wrap gap-2 mb-4">
            {listing.baggage_items?.length > 0 ? (
              listing.baggage_items
                .filter(item => item.quantity > 0)
                .map((item, idx) => {
                  const avail = item.available_quantity ?? item.quantity;
                  const pct = item.quantity > 0 ? (avail / item.quantity) * 100 : 0;
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/40 border border-border text-xs"
                    >
                      <Luggage className="w-3 h-3 text-[#D4AF37]/70" />
                      <span className="text-foreground">
                        {avail}/{item.quantity} {BAGGAGE_TYPE_SHORT[item.type] || item.type}
                      </span>
                      <span className="text-foreground/30">|</span>
                      <span className="text-[#D4AF37] font-medium">
                        {item.price_per_unit} {listing.currency || 'CAD'}/{item.type === 'kilos' ? 'kg' : 'unité'}
                      </span>
                      {/* Mini availability bar */}
                      <div className="w-8 h-1 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full ${pct > 50 ? 'bg-emerald-500' : pct > 20 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/40 border border-border text-xs">
                <Luggage className="w-3 h-3 text-[#D4AF37]/70" />
                <span className="text-foreground">
                  {listing.available_quantity}/{listing.quantity} {BAGGAGE_TYPE_SHORT[listing.baggage_type] || listing.baggage_type}
                </span>
                <span className="text-foreground/30">|</span>
                <span className="text-[#D4AF37] font-medium">
                  {listing.price_per_unit} {listing.currency || 'CAD'}
                </span>
              </div>
            )}
          </div>

          {/* Pickup & Delivery cities */}
          {(listing.pickup_cities?.length > 0 || listing.delivery_cities?.length > 0) && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              {listing.pickup_cities?.length > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Dépôt : {listing.pickup_cities.join(', ')}
                </span>
              )}
              {listing.delivery_cities?.length > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Récupération : {listing.delivery_cities.join(', ')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right: Price, rating, CTA */}
        <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between gap-4 lg:min-w-[180px] pt-2 lg:pt-0 border-t lg:border-t-0 lg:border-l border-border lg:pl-5">
          {/* Rating */}
          <div className="flex items-center gap-2 text-sm" data-testid={`listing-rating-${listing.id}`}>
            <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
            <span className="text-foreground/70 font-medium">{listing.seller_rating?.toFixed(1) || '0.0'}</span>
            <Badge className="bg-muted/50 text-muted-foreground border-border text-[10px] px-1.5">
              {listing.seller_grade}
            </Badge>
          </div>

          {/* Price */}
          <div className="text-right lg:text-right" data-testid={`listing-price-${listing.id}`}>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">À partir de</p>
            <p className="font-heading text-xl sm:text-2xl font-bold text-[#D4AF37]">
              {minPrice} {listing.currency || 'CAD'}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              par {listing.baggage_items?.length > 1 ? 'kg (min.)' : 'kg'}
            </p>
          </div>

          {/* Urgency indicators */}
          <div className="flex flex-col items-end gap-1.5">
            {isUrgent && (
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full" data-testid={`listing-urgent-${listing.id}`}>
                <Clock className="w-3 h-3" />
                Départ dans {daysUntil}j
              </span>
            )}
            {isLowStock && (
              <span className="inline-flex items-center gap-1 text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full" data-testid={`listing-lowstock-${listing.id}`}>
                <TrendingUp className="w-3 h-3" />
                {availKg} kg restants
              </span>
            )}
          </div>

          {/* CTA */}
          <Button
            className="w-full lg:w-auto px-5 h-10 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold text-sm rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md shadow-[#D4AF37]/10 group-hover:shadow-lg group-hover:shadow-[#D4AF37]/20 whitespace-nowrap"
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            data-testid={`listing-book-btn-${listing.id}`}
          >
            <span className="hidden sm:inline">Réserver maintenant</span>
            <span className="sm:hidden">Réserver</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── TRUST SECTION ───────────────────────────────────────────────────
const TrustSection = () => (
  <section className="bg-background border-t border-border py-16 sm:py-20" data-testid="trust-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-3">
          Pourquoi choisir <span className="text-[#D4AF37]">KACOS</span>
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Une plateforme fiable pour vos envois entre le Cameroun et le Canada
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            icon: Shield,
            title: 'Paiement sécurisé',
            desc: 'Vos fonds sont protégés en séquestre jusqu\'à la confirmation de livraison.',
          },
          {
            icon: Package,
            title: 'Gestion logistique',
            desc: 'KACOS coordonne le ramassage, le suivi et la livraison de vos colis.',
          },
          {
            icon: Headphones,
            title: 'Support client',
            desc: 'Une équipe disponible pour vous accompagner à chaque étape du processus.',
          },
          {
            icon: BadgeCheck,
            title: 'Processus fiable',
            desc: 'Voyageurs vérifiés, billets validés et avis transparents de la communauté.',
          },
        ].map(({ icon: Icon, title, desc }) => (
          <div
            key={title}
            className="group p-6 rounded-2xl bg-white/[0.02] border border-border hover:border-[#D4AF37]/20 transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/15 transition-colors">
              <Icon className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <h3 className="font-heading font-semibold text-foreground mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── MAIN PAGE ───────────────────────────────────────────────────────
const ListingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    departure_city: '',
    destination_city: '',
    travel_date: null,
    departure_country: '',
    destination_country: '',
    max_price: '',
    verified_only: false,
  });

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.departure_city) params.append('departure_city', filters.departure_city);
      if (filters.destination_city) params.append('destination_city', filters.destination_city);
      if (filters.travel_date) params.append('travel_date', format(filters.travel_date, 'yyyy-MM-dd'));
      if (filters.max_price) params.append('max_price', filters.max_price);
      const response = await axios.get(`${API}/listings?${params.toString()}`);
      setAllListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filters (country, verified)
  const filteredListings = useMemo(() => {
    let result = [...allListings];
    if (filters.departure_country) {
      result = result.filter(l => l.departure_country === filters.departure_country);
    }
    if (filters.destination_country) {
      result = result.filter(l => l.destination_country === filters.destination_country);
    }
    if (filters.verified_only) {
      result = result.filter(l => l.seller_verified);
    }
    return result;
  }, [allListings, filters.departure_country, filters.destination_country, filters.verified_only]);

  const clearAdvancedFilters = () => {
    setFilters(f => ({ ...f, departure_country: '', destination_country: '', max_price: '', verified_only: false }));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="listings-page">
      <Navbar />

      {/* Hero */}
      <HeroSection filters={filters} setFilters={setFilters} onSearch={fetchListings} navigate={navigate} user={user} />

      {/* Filter bar */}
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        onSearch={fetchListings}
        onClear={clearAdvancedFilters}
        count={filteredListings.length}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        allListings={allListings}
      />

      {/* Listings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="listings-section">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-10 h-10 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Chargement des offres...</p>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4" data-testid="no-results">
            <div className="w-16 h-16 rounded-2xl bg-muted/40 flex items-center justify-center">
              <Luggage className="w-8 h-8 text-foreground/20" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground/70">Aucune offre disponible</h3>
            <p className="text-sm text-muted-foreground max-w-sm text-center">
              Essayez de modifier vos critères de recherche ou revenez plus tard.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="listings-list">
            {filteredListings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                onClick={() => navigate(`/listings/${listing.id}`)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Trust Section */}
      <TrustSection />

      <Footer />
    </div>
  );
};

export default ListingsPage;
